import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import BrokenImageOutlinedIcon from '@mui/icons-material/BrokenImageOutlined';
import CloseIcon from '@mui/icons-material/Close';

import { getAttachmentUrl } from '../../utils/attachmentsApi';

const THUMB = 44;

/**
 * Resolves an attachment's signed URL and keeps it fresh.
 *
 * The URL is deliberately short-lived, so it is fetched when the image is about
 * to be shown rather than stored with the record, and refreshed just before it
 * lapses. A tab left open on a bet would otherwise start showing broken images
 * a few minutes in.
 */
function useAttachmentUrl(attachmentId, active = true) {
  const [url, setUrl] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!active) return undefined;

    let cancelled = false;
    let timer;

    const load = async () => {
      try {
        const data = await getAttachmentUrl(attachmentId);
        if (cancelled) return;
        setUrl(data.url);
        setFailed(false);
        // Renew a little early so the swap happens behind a working image.
        const ms = Math.max(30, (data.expiresInSeconds || 300) - 30) * 1000;
        timer = setTimeout(load, ms);
      } catch {
        if (!cancelled) setFailed(true);
      }
    };

    load();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [attachmentId, active]);

  return { url, failed };
}

const Thumb = ({ attachment, canEdit, onOpen, onDelete, busy }) => {
  const { url, failed } = useAttachmentUrl(attachment.id);
  const [hovered, setHovered] = useState(false);

  return (
    <Box
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      sx={{
        position: 'relative',
        width: THUMB,
        height: THUMB,
        borderRadius: 1,
        overflow: 'hidden',
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'action.hover',
        flexShrink: 0,
      }}
    >
      {failed ? (
        <Tooltip title="This image could not be loaded">
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>
            <BrokenImageOutlinedIcon fontSize="small" color="disabled" />
          </Box>
        </Tooltip>
      ) : url ? (
        <Box
          component="img"
          src={url}
          alt={attachment.filename}
          onClick={() => onOpen(attachment)}
          sx={{ width: '100%', height: '100%', objectFit: 'cover', cursor: 'zoom-in', display: 'block' }}
        />
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>
          <CircularProgress size={14} />
        </Box>
      )}

      {canEdit && hovered && !busy && (
        <Tooltip title="Remove image">
          <IconButton
            size="small"
            aria-label={`Remove ${attachment.filename}`}
            onClick={() => onDelete(attachment)}
            sx={{
              position: 'absolute',
              top: 0,
              right: 0,
              p: '1px',
              bgcolor: 'rgba(0,0,0,.55)',
              color: 'common.white',
              '&:hover': { bgcolor: 'rgba(0,0,0,.75)' },
            }}
          >
            <CloseIcon sx={{ fontSize: 12 }} />
          </IconButton>
        </Tooltip>
      )}

      {busy && (
        <Box sx={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'rgba(0,0,0,.35)',
        }}>
          <CircularProgress size={14} sx={{ color: 'common.white' }} />
        </Box>
      )}
    </Box>
  );
};

/** The full image, in a dialog, with its own freshly signed URL. */
const Lightbox = ({ attachment, onClose }) => {
  const { url, failed } = useAttachmentUrl(attachment?.id, Boolean(attachment));

  return (
    <Dialog open={Boolean(attachment)} onClose={onClose} maxWidth="lg">
      <DialogContent sx={{ p: 0, position: 'relative', bgcolor: 'background.default' }}>
        <IconButton
          onClick={onClose}
          aria-label="Close image"
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            bgcolor: 'rgba(0,0,0,.55)',
            color: 'common.white',
            '&:hover': { bgcolor: 'rgba(0,0,0,.75)' },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>

        {failed ? (
          <Box sx={{ p: 6, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              This image could not be loaded.
            </Typography>
          </Box>
        ) : url ? (
          <Box
            component="img"
            src={url}
            alt={attachment?.filename}
            sx={{ display: 'block', maxWidth: '90vw', maxHeight: '85vh' }}
          />
        ) : (
          <Box sx={{ p: 8, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress size={24} />
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

/**
 * The images on a scratchpad task or a manual scope todo, as a row of
 * thumbnails that open full size when clicked.
 *
 * Renders nothing at all when there are no images, so a task without one looks
 * exactly as it did before the feature existed.
 */
const AttachmentStrip = ({ attachments = [], canEdit = false, onDelete, deletingId = null }) => {
  const [open, setOpen] = useState(null);

  const handleDelete = useCallback((attachment) => {
    if (onDelete) onDelete(attachment);
  }, [onDelete]);

  if (!attachments.length) return null;

  return (
    <>
      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
        {attachments.map(attachment => (
          <Thumb
            key={attachment.id}
            attachment={attachment}
            canEdit={canEdit}
            onOpen={setOpen}
            onDelete={handleDelete}
            busy={deletingId === attachment.id}
          />
        ))}
      </Box>
      <Lightbox attachment={open} onClose={() => setOpen(null)} />
    </>
  );
};

export default AttachmentStrip;
